'use client';

import React, { useEffect, useState } from 'react';
import { Sparkles, ExternalLink } from 'lucide-react';
import { cn } from '@/lib/utils';
import { getDailyNewsSummary, type PublicNewsDailySummary } from '@/lib/api-client';

/** AI-generated end-of-day summary card for the public /news page. Hides
 *  itself silently when no summary has been generated for today. Reads the
 *  latest row (any date), so admins can manually regenerate on a quiet
 *  news day without the card disappearing.
 */
export function DailyNewsSummaryCard() {
  const [summary, setSummary] = useState<PublicNewsDailySummary | null>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    getDailyNewsSummary()
      .then((s) => { if (!cancelled) setSummary(s); })
      .catch(() => { if (!cancelled) setSummary(null); })
      .finally(() => { if (!cancelled) setLoaded(true); });
    return () => { cancelled = true; };
  }, []);

  // Render nothing while loading (no skeleton — the card is optional) and
  // nothing when the summary is missing so the existing news feed still
  // leads the page on days when no summary exists.
  if (!loaded || !summary) return null;

  const toneStyles: Record<NonNullable<PublicNewsDailySummary['marketTone']>, string> = {
    bullish: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/40',
    neutral: 'bg-slate-700 text-slate-300 border-slate-600',
    bearish: 'bg-red-500/15 text-red-300 border-red-500/40',
  };

  return (
    <div className="bg-gradient-to-br from-sky-500/5 to-violet-500/5 border border-sky-500/20 rounded-xl p-5 mb-6">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex items-center gap-2">
          <div className="w-7 h-7 rounded-full bg-sky-500/15 border border-sky-500/40 flex items-center justify-center">
            <Sparkles size={14} className="text-sky-300" />
          </div>
          <div>
            <h2 className="text-base font-bold text-slate-100">Today&apos;s AI Digest</h2>
            <p className="text-[10px] text-fg-subtle font-mono">{summary.date}</p>
          </div>
        </div>
        {summary.marketTone && (
          <span className={cn(
            'text-[10px] font-bold uppercase tracking-wide px-2 py-1 rounded border',
            toneStyles[summary.marketTone],
          )}>
            Tone: {summary.marketTone}
          </span>
        )}
      </div>

      <p className="text-[13px] sm:text-sm text-slate-200 leading-relaxed mb-4">{summary.digest}</p>

      {summary.picks.length > 0 && (
        <div>
          <div className="text-[10px] uppercase tracking-wide text-fg-subtle mb-2">Top stories</div>
          <ol className="space-y-1.5">
            {summary.picks.map((p) => (
              <li key={p.rank} className="flex gap-2 items-start text-[12px] sm:text-[13px]">
                <span className="font-mono font-bold text-sky-400 w-5 flex-shrink-0">{p.rank}.</span>
                <div className="min-w-0 flex-1">
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-start gap-1 text-slate-100 hover:text-sky-300 font-medium transition-colors"
                  >
                    {p.headline}
                    <ExternalLink size={10} className="mt-1 flex-shrink-0 text-fg-subtle" />
                  </a>
                  <div className="text-[11px] text-fg-subtle mt-0.5">
                    {p.note}
                    {p.tickers.length > 0 && (
                      <>
                        {' · '}
                        {p.tickers.map((t, i) => (
                          <span key={t} className="font-mono font-semibold text-sky-400">
                            {t}{i < p.tickers.length - 1 ? ', ' : ''}
                          </span>
                        ))}
                      </>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-sky-500/10 text-[9px] text-slate-600 font-mono">
        Generated by Claude · Editorial summary, not investment advice.
      </div>
    </div>
  );
}
